/**
 * ClubOrderReview.js — Club signup order review (kind "club-order-review").
 *
 * Structural parity with the cart checkout's own order review
 * (variants/valledellapace/components/OrderReview.js): a "Ship to"/
 * "Pickup by" group (own Edit) and a "Payment" group, ahead of the wine
 * items + totals. Shown AFTER Ship/Pickup + the address form are collected
 * (club_c7.js's handleAddressSubmit), so the virtual-cart response's
 * shipTo/pickupBy details are already resolved by the time this renders.
 *
 * The virtual cart itself carries no tender yet (club checkout doesn't
 * attach a card until the payment step after this review), so the Payment
 * group is built from the customer's saved cards (savedCards, fetched by
 * handleAddressSubmit the same way the payment step itself does) rather
 * than cart.tenders — showing the card that'll actually be charged next.
 *
 * "Continue..." moves on to payment. Edit on the Ship to/Pickup by group
 * re-shows the Ship/Pickup choice step (not straight to the address/pickup
 * form) — the customer might want to switch delivery type, not just tweak
 * the same one's details (see club_c7.js's handleChangeDelivery).
 *
 * message.data: { cart: <Commerce7 signup virtual-cart response — items,
 *   subTotal/shippingTotal/taxTotal/total, shipTo|pickupBy>, clubTitle,
 *   type: "ship"|"pickup", savedCards: [...] }
 *
 * EVENTS OUT: ws-club-continue-checkout, ws-club-checkout-edit
 */

import { emit } from "../../../core/state.js";

const EDIT_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor"/><path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/></svg>`;

function cleanDisplay(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "undefined" || text === "null" ? "" : text;
}

function groupHead(titleText, onEdit) {
  const head = document.createElement("div");
  head.className = "ws-order-head";
  const title = document.createElement("p");
  title.className = "ws-order-group-title";
  title.textContent = titleText;
  head.appendChild(title);
  if (onEdit) {
    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "ws-order-edit";
    edit.innerHTML = EDIT_ICON;
    edit.addEventListener("click", onEdit);
    head.appendChild(edit);
  }
  return head;
}

function detailBody() {
  const el = document.createElement("div");
  el.className = "ws-order-details";
  return el;
}

function textLine(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div;
}

export default class ClubOrderReview {
  render(message) {
    const cart = message?.data?.cart || {};
    const clubTitle = message?.data?.clubTitle || "";
    const type = message?.data?.type === "pickup" ? "pickup" : "ship";
    const items = Array.isArray(cart.items) ? cart.items : [];
    const savedCards = Array.isArray(message?.data?.savedCards) ? message.data.savedCards : [];
    const details = (type === "pickup" ? cart.pickupBy : cart.shipTo) || {};

    const wrap = document.createElement("div");
    wrap.className = "ws-order-review";

    const title = document.createElement("p");
    title.className = "ws-order-title";
    title.textContent = clubTitle ? `${clubTitle} — Order Summary` : "Order Summary";
    wrap.appendChild(title);

    const divider = document.createElement("hr");
    divider.className = "ws-order-divider";
    wrap.appendChild(divider);

    // ── Group 1: Ship to / Pickup by ──
    const group = document.createElement("div");
    group.className = "ws-order-summary-group";
    group.appendChild(
      groupHead(type === "pickup" ? "Pickup by" : "Ship to", () =>
        emit("ws-club-checkout-edit"),
      ),
    );
    const body = detailBody();

    if (type === "pickup") {
      // Pickup by — full pickup-location address (cart.pickupBy is the
      // Commerce7 pickup-location object: title, address, city, stateCode,
      // zipCode, countryCode, phone), same field names/order as
      // MembershipDetails.js's "Pickup by" and OrderReview.js's pickup
      // group — not just firstName/lastName.
      const name = [details.firstName, details.lastName]
      .map(cleanDisplay)
      .filter(Boolean)
      .join(" ");

      if (name) body.appendChild(textLine(name));
      if (cleanDisplay(details.title)) body.appendChild(textLine(details.title));

      const addr = [details.address, details.address2]
        .map(cleanDisplay)
        .filter(Boolean)
        .join(" ");
      if (addr) body.appendChild(textLine(addr));

      const stateCityLine = [details.stateCode, details.city]
        .map(cleanDisplay)
        .filter(Boolean)
        .join(", ");
      if (stateCityLine) body.appendChild(textLine(stateCityLine));

      const countryZipLine = [details.countryCode, details.zipCode]
        .map(cleanDisplay)
        .filter(Boolean)
        .join("-");
      if (countryZipLine) body.appendChild(textLine(countryZipLine));

      if (cleanDisplay(details.phone)) {
        body.appendChild(textLine(`Phone: ${details.phone}`));
      }
    } else {
      // Ship — recipient name/company/address, unchanged.
      const name = [details.firstName, details.lastName]
        .map(cleanDisplay)
        .filter(Boolean)
        .join(" ");
      if (name) body.appendChild(textLine(name));
      if (cleanDisplay(details.company)) body.appendChild(textLine(details.company));

      const addr = [details.address, details.address2]
        .map(cleanDisplay)
        .filter(Boolean)
        .join(" ");
      if (addr) body.appendChild(textLine(addr));

      const cityLine = [
        cleanDisplay(details.city),
        [details.stateCode, details.zipCode].map(cleanDisplay).filter(Boolean).join("-"),
      ]
        .filter(Boolean)
        .join(", ");
      if (cityLine) body.appendChild(textLine(cityLine));

      if (cleanDisplay(details.countryCode)) body.appendChild(textLine(details.countryCode));

      if (cleanDisplay(details.phone)) {
        body.appendChild(textLine(`Phone: ${details.phone}`));
      }
    }

    group.appendChild(body);
    wrap.appendChild(group);

    // ── Group 2: Payment (the saved card that'll be charged next) ──
    if (savedCards.length) {
      const payGroup = document.createElement("div");
      payGroup.className = "ws-order-summary-group";
      payGroup.appendChild(
        groupHead("Payment", () => emit("ws-club-continue-checkout")),
      );
      const card = savedCards.find((c) => c.isDefault) || savedCards[0];
      const item = document.createElement("div");
      item.className = "ws-order-tender-item";
      const brand = document.createElement("div");
      brand.style.fontWeight = "600";
      brand.style.textTransform = "uppercase";
      brand.textContent = card.cardBrand || "Card";
      item.appendChild(brand);
      const meta = document.createElement("div");
      meta.style.fontSize = "12px";
      meta.textContent = `${card.maskedCardNumber || ""} | Expiry: ${card.expiryMo}/${card.expiryYr}`;
      item.appendChild(meta);
      payGroup.appendChild(item);
      wrap.appendChild(payGroup);
    }

    // ── Group 3: Cart details ──
    const cartGroup = document.createElement("div");
    cartGroup.className = "ws-order-summary-group";
    const cartHead = document.createElement("div");
    cartHead.className = "ws-order-head";
    const cartTitle = document.createElement("p");
    cartTitle.className = "ws-order-group-title";
    cartTitle.textContent = "Cart details";
    cartHead.appendChild(cartTitle);
    cartGroup.appendChild(cartHead);
    items.forEach((p) => cartGroup.appendChild(this._productCard(p)));
    wrap.appendChild(cartGroup);

    const totals = document.createElement("div");
    totals.className = "ws-order-totals";

    const hr1 = document.createElement("hr");
    hr1.className = "ws-order-divider";
    totals.appendChild(hr1);

    const table = document.createElement("div");
    table.className = "ws-order-totals-table";
    if (cart.subTotal) table.appendChild(this._totalRow("Subtotal", cart.subTotal));
    if (cart.shippingTotal) table.appendChild(this._totalRow("Shipping", cart.shippingTotal));
    if (cart.taxTotal) table.appendChild(this._totalRow("Taxes", cart.taxTotal));
    totals.appendChild(table);

    const hr2 = document.createElement("hr");
    hr2.className = "ws-order-divider";
    totals.appendChild(hr2);

    const est = document.createElement("div");
    est.className = "ws-order-estimated";
    const estLabel = document.createElement("div");
    estLabel.textContent = "Estimated total";
    const estVal = document.createElement("div");
    estVal.textContent = cart.total || "";
    est.appendChild(estLabel);
    est.appendChild(estVal);
    totals.appendChild(est);

    wrap.appendChild(totals);

    const footer = document.createElement("div");
    footer.className = "ws-order-footer";
    const cont = document.createElement("button");
    cont.type = "button";
    cont.className = "ws-order-checkout-btn";
    cont.textContent = "Continue...";
    cont.addEventListener("click", () => {
      cont.disabled = true;
      emit("ws-club-continue-checkout");
    });
    footer.appendChild(cont);
    wrap.appendChild(footer);

    return wrap;
  }

  _productCard(p) {
    const line = document.createElement("div");
    line.className = "ws-order-item";

    if (p.image) {
      const imgWrap = document.createElement("div");
      imgWrap.className = "ws-order-item-img-wrap";
      const img = document.createElement("img");
      img.className = "ws-order-item-img";
      img.src = p.image;
      img.alt = p.productTitle || "Item";
      img.loading = "lazy";
      imgWrap.appendChild(img);
      line.appendChild(imgWrap);
    }

    const body = document.createElement("div");
    body.className = "ws-order-item-body";

    const t = document.createElement("strong");
    t.className = "ws-order-item-title";
    t.textContent = p.productTitle || "";
    body.appendChild(t);

    const desc = document.createElement("p");
    desc.className = "ws-order-item-desc";
    desc.textContent = [p.productVariantTitle, `${p.price || ""} per bottle`]
      .filter(Boolean)
      .join(" | ");
    body.appendChild(desc);

    const footer = document.createElement("div");
    footer.className = "ws-order-item-footer";
    const qty = document.createElement("p");
    qty.className = "ws-order-item-qty";
    qty.textContent = `${p.price || ""} X ${p.quantity ?? 1}`;
    const price = document.createElement("p");
    price.className = "ws-order-item-price";
    price.textContent = p.extPrice || p.price || "";
    footer.appendChild(qty);
    footer.appendChild(price);
    body.appendChild(footer);

    line.appendChild(body);
    return line;
  }

  _totalRow(label, value) {
    const row = document.createElement("div");
    row.className = "ws-order-total-row";
    const l = document.createElement("span");
    l.textContent = label;
    const v = document.createElement("span");
    v.textContent = value;
    row.appendChild(l);
    row.appendChild(v);
    return row;
  }
}